import {
  CostInsightsApi,
  ProductInsightsOptions,
  Alert,
  DEFAULT_DATE_FORMAT,
} from '@backstage-community/plugin-cost-insights';
import {
  Cost,
  Entity,
  Group,
  MetricData,
  Project,
} from '@backstage-community/plugin-cost-insights-common';
import { ConfigApi, IdentityApi } from '@backstage/core-plugin-api';
import { DateTime } from 'luxon';

export class CostInsightsClient implements CostInsightsApi {
  private readonly configApi: ConfigApi;
  private readonly identityApi: IdentityApi;

  constructor(options: { configApi: ConfigApi; identityApi: IdentityApi }) {
    this.configApi = options.configApi;
    this.identityApi = options.identityApi;
  }

  async getLastCompleteBillingDate(): Promise<string> {
    return DateTime.now().minus({ days: 1 }).toFormat(DEFAULT_DATE_FORMAT);
  }

  async getUserGroups(_userId: string): Promise<Group[]> {
    return [{ id: 'dev-prod', name: 'Development Productivity;' }];
  }

  // todo: retrun the project depending on the user
  async getGroupProjects(_group: string): Promise<Project[]> {
    try {
      const { token } = await this.identityApi.getCredentials();

      const requestHeaders: HeadersInit = {
        'Content-Type': 'application/json',
      };

      if (token) {
        (requestHeaders as Record<string, string>).Authorization =
          `Bearer ${token}`;
      }

      const backendUrl = this.configApi.getString('backend.baseUrl');
      const response = await fetch(
        `${backendUrl}/api/cost-insights-backend/project-list`,
        {
          headers: requestHeaders,
        },
      );
      const data: Project[] = await response.json();

      return data;
    } catch (error) {
      throw new Error('Failed to fetch project list from backend.');
    }
  }

  async getAlerts(_group: string): Promise<Alert[]> {
    return [];
  }

  async getDailyMetricData(
    _metric: string,
    _intervals: string,
  ): Promise<MetricData> {
    return {
      id: 'remove-me',
      format: 'number',
      aggregation: [],
      change: {
        ratio: 0,
        amount: 0,
      },
    };
  }

  // todo: get all the costs for all the projects depending on the user
  async getGroupDailyCost(_group: string, _intervals: string): Promise<Cost> {
    const backendUrl = this.configApi.getString('backend.baseUrl');

    try {
      const { token } = await this.identityApi.getCredentials();

      const requestHeaders: HeadersInit = {
        'Content-Type': 'application/json',
      };

      if (token) {
        (requestHeaders as Record<string, string>).Authorization =
          `Bearer ${token}`;
      }

      const response = await fetch(
        `${backendUrl}/api/cost-insights-backend/cost-data?intervals=${_intervals}`,
        {
          headers: requestHeaders,
        },
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `Failed to fetch cost data: ${response.status} ${errorText}`,
        );
      }

      const data: Cost = await response.json();

      return data;
    } catch (error) {
      throw new Error('Failed to fetch all projects cost data from backend.');
    }
  }

  async getProjectDailyCost(project: string, intervals: string): Promise<Cost> {
    const backendUrl = this.configApi.getString('backend.baseUrl');

    try {
      const { token } = await this.identityApi.getCredentials();

      const requestHeaders: HeadersInit = {
        'Content-Type': 'application/json',
      };

      if (token) {
        (requestHeaders as Record<string, string>).Authorization =
          `Bearer ${token}`;
      }

      const response = await fetch(
        `${backendUrl}/api/cost-insights-backend/cost-data/${project}?intervals=${intervals}`,
        {
          headers: requestHeaders,
        },
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `Failed to fetch cost data: ${response.status} ${errorText}`,
        );
      }

      const data: Cost = await response.json();
      return data;
    } catch (error) {
      throw new Error('Failed to fetch cost data from backend.');
    }
  }

  async getCatalogEntityDailyCost(
    _catalogEntityRef: string,
    _intervals: string,
  ): Promise<Cost> {
    return {
      id: 'remove-me',
      aggregation: [],
      change: {
        ratio: 0,
        amount: 0,
      },
    };
  }

  async getProductInsights(options: ProductInsightsOptions): Promise<Entity> {
    const backendUrl = this.configApi.getString('backend.baseUrl');

    try {
      const { token } = await this.identityApi.getCredentials();

      const requestHeaders: HeadersInit = {
        'Content-Type': 'application/json',
      };

      if (token) {
        (requestHeaders as Record<string, string>).Authorization =
          `Bearer ${token}`;
      }

      const response = await fetch(
        `${backendUrl}/api/cost-insights-backend/product-insights/${options.product}?intervals=${options.intervals}&group=${options.group}&project=${options.project}`,
        {
          headers: requestHeaders,
        },
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `Failed to fetch product insights: ${response.status} ${errorText}`,
        );
      }

      const data: Entity = await response.json();
      return data;
    } catch (error) {
      throw new Error('Failed to fetch product insights from backend.');
    }
  }
}
